import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  BrowserRouter,
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import {
  AnimatePresence,
  animate,
  motion,
  useInView,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from "motion/react";
import { ReactLenis, useLenis } from "lenis/react";
import { GridMark } from "./components/GridMark.jsx";
import { PageEnding } from "./components/PageEnding.jsx";
import "lenis/dist/lenis.css";
import {
  ArrowDown,
  ArrowRight,
  ArrowUpRight,
  BookOpenText,
  Briefcase,
  Buildings,
  CalendarBlank,
  CaretDown,
  ChartLineUp,
  ChatCircleText,
  Check,
  CheckCircle,
  Compass,
  DiscordLogo,
  EnvelopeSimple,
  Eye,
  Flag,
  Hammer,
  Handshake,
  Images,
  Info,
  InstagramLogo,
  Lightbulb,
  LinkedinLogo,
  List,
  MagnifyingGlass,
  MapTrifold,
  Megaphone,
  Path,
  Play,
  Plus,
  RocketLaunch,
  Scales,
  SealCheck,
  Sparkle,
  Student,
  Target,
  UsersThree,
  Wrench,
  X,
} from "@phosphor-icons/react";
import {
  aarohCollaborators,
  buildSteps,
  coreTeamGroups,
  fieldNotes,
  guides,
  impactAchievements,
  impactMetrics,
  opportunityPaths,
  peopleMet,
  problemCategories,
  programmes,
  stories,
  verifiedPartnerRelationships,
} from "./data/content.js";
import {
  routeGroups,
  routeLabel,
  siteRoutes,
} from "./data/routes.js";
import {
  DEFAULT_SEO_IMAGE,
  MEDIA_LIBRARY,
  ORGANIZATION_LOGO,
  SITE_URL,
  getImageData,
  imageForRoute,
  organizationGraph,
} from "./data/seo.js";
import { AAROH_IMAGES } from "./data/images.js";

export const CAMPAIGN_IMAGES = {
  hero: AAROH_IMAGES.room,
  foundersCommunity: AAROH_IMAGES.collaboration,
  speaker: AAROH_IMAGES.studentSpeaker,
  community: AAROH_IMAGES.roomWide,
  makerspace: AAROH_IMAGES.buildSession,
  founders: "/images/grid-photo-09-p8.jpg",
  builders: AAROH_IMAGES.studentDiscussion,
  cohort: AAROH_IMAGES.roomWide,
  builderProfile: AAROH_IMAGES.studentPortrait,
  founderProfile: AAROH_IMAGES.studentTeam,
  impactCrowd: AAROH_IMAGES.roomWide,
  recognition: AAROH_IMAGES.recognition,
  flagship: "/images/grid-photo-04-p7.jpg",
  auroraWinners: "/images/campaign/optimized/grid-campaign-11-aurora-winners.avif",
};
export const IMAGEN_ASSETS = {
  accessPathways: "/images/imagen/grid-access-pathways-v1.webp",
  halftoneOverlay: "/images/imagen/grid-halftone-overlay-v1.webp",
  handmarksOverlay: "/images/imagen/grid-handmarks-overlay-v1.webp",
};
export function ThemeProvider({ children }) {
  useEffect(() => {
    document.documentElement.dataset.theme = "light";
    document.documentElement.style.colorScheme = "light";
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", "#ffffff");
  }, []);

  return children;
}

export function ensureMeta(selector, attrs) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }
  Object.entries(attrs).forEach(([key, value]) => element.setAttribute(key, value));
}

export function buildBreadcrumbSchema(path) {
  if (path === "/") return null;

  const labels = {
    aaroh: "AAROH",
    aurora: "Aurora",
    "civic-tech": "Civic tech",
    internships: "Internships",
    metamorphosis: "Metamorphosis",
    organisations: "Organisations",
    partnerships: "Partnerships",
    programmes: "Programmes",
    "student-opportunities": "Student opportunities",
  };
  const segments = path.split("/").filter(Boolean);

  return {
    "@type": "BreadcrumbList",
    "@id": `${SITE_URL}${path}#breadcrumb`,
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Project GRID",
        item: `${SITE_URL}/`,
      },
      ...segments.map((segment, index) => ({
        "@type": "ListItem",
        position: index + 2,
        name:
          labels[segment] ??
          segment
            .split("-")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" "),
        item: `${SITE_URL}/${segments.slice(0, index + 1).join("/")}`,
      })),
    ],
  };
}

export function buildPageSchema({ canonicalUrl, description, fullTitle, image, path, schema }) {
  const primaryImage = image ?? imageForRoute(path);
  const pageSchema = {
    "@type": "WebPage",
    "@id": `${canonicalUrl}#webpage`,
    url: canonicalUrl,
    name: fullTitle,
    description,
    inLanguage: "en-IN",
    isPartOf: { "@id": `${SITE_URL}/#website` },
    about: { "@id": `${SITE_URL}/#organization` },
    primaryImageOfPage: {
      "@type": "ImageObject",
      url: `${SITE_URL}${primaryImage.src}`,
      contentUrl: `${SITE_URL}${primaryImage.src}`,
      width: primaryImage.width,
      height: primaryImage.height,
      caption: primaryImage.alt,
    },
  };
  const breadcrumb = buildBreadcrumbSchema(path);
  const baseGraph = organizationGraph();

  if (!schema) {
    return {
      "@context": "https://schema.org",
      "@graph": [...baseGraph, pageSchema, ...(breadcrumb ? [breadcrumb] : [])],
    };
  }

  if (schema["@graph"]) {
    const supplementalGraph = schema["@graph"].filter(
      (item) =>
        item["@id"] !== `${SITE_URL}/#organization` &&
        item["@id"] !== `${SITE_URL}/#website`,
    );
    return {
      "@context": "https://schema.org",
      "@graph": [...baseGraph, ...supplementalGraph, pageSchema, ...(breadcrumb ? [breadcrumb] : [])],
    };
  }

  const { "@context": _context, ...schemaWithoutContext } = schema;
  return {
    "@context": "https://schema.org",
    "@graph": [...baseGraph, schemaWithoutContext, pageSchema, ...(breadcrumb ? [breadcrumb] : [])],
  };
}

export function Seo({
  title = "Project GRID | Student Innovation Programs in India",
  description = "Project GRID is a youth-led innovation ecosystem helping students in Grades 8-12 find real-world problems, build prototypes and create practical solutions.",
  path = "/",
  type = "website",
  schema,
  image,
}) {
  useEffect(() => {
    const fullTitle = title.includes("Project GRID") ? title : `${title} | Project GRID`;
    const canonicalUrl = `${SITE_URL}${path}`;
    const pageImage = image ?? imageForRoute(path);
    const socialImage = `${SITE_URL}${pageImage.src}`;
    const robots =
      path === "/privacy" || path === "/terms" || path === "/404"
        ? "noindex, follow"
        : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

    document.title = fullTitle;
    ensureMeta('meta[name="description"]', { name: "description", content: description });
    ensureMeta('meta[name="robots"]', { name: "robots", content: robots });
    ensureMeta('meta[name="googlebot"]', { name: "googlebot", content: robots });
    ensureMeta('meta[name="author"]', { name: "author", content: "Project GRID" });
    ensureMeta('meta[property="og:title"]', { property: "og:title", content: fullTitle });
    ensureMeta('meta[property="og:description"]', {
      property: "og:description",
      content: description,
    });
    ensureMeta('meta[property="og:type"]', { property: "og:type", content: type });
    ensureMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
    ensureMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "Project GRID" });
    ensureMeta('meta[property="og:locale"]', { property: "og:locale", content: "en_IN" });
    ensureMeta('meta[property="og:image"]', {
      property: "og:image",
      content: socialImage,
    });
    ensureMeta('meta[property="og:image:secure_url"]', {
      property: "og:image:secure_url",
      content: socialImage,
    });
    ensureMeta('meta[property="og:image:width"]', {
      property: "og:image:width",
      content: String(pageImage.width),
    });
    ensureMeta('meta[property="og:image:height"]', {
      property: "og:image:height",
      content: String(pageImage.height),
    });
    ensureMeta('meta[property="og:image:alt"]', {
      property: "og:image:alt",
      content: pageImage.alt,
    });
    ensureMeta('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    ensureMeta('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: fullTitle,
    });
    ensureMeta('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: description,
    });
    ensureMeta('meta[name="twitter:image"]', {
      name: "twitter:image",
      content: socialImage,
    });
    ensureMeta('meta[name="twitter:image:alt"]', {
      name: "twitter:image:alt",
      content: pageImage.alt,
    });

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    let llmsLink = document.head.querySelector('link[rel="alternate"][type="text/plain"]');
    if (!llmsLink) {
      llmsLink = document.createElement("link");
      llmsLink.rel = "alternate";
      llmsLink.type = "text/plain";
      document.head.appendChild(llmsLink);
    }
    llmsLink.href = `${SITE_URL}/llms.txt`;
    llmsLink.title = "Project GRID for AI systems";

    const scriptId = "grid-page-schema";
    document.getElementById(scriptId)?.remove();
    document.getElementById("grid-static-schema")?.remove();
    const script = document.createElement("script");
    script.id = scriptId;
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(
      buildPageSchema({ canonicalUrl, description, fullTitle, image: pageImage, path, schema }),
    );
    document.head.appendChild(script);
  }, [description, image, path, schema, title, type]);

  return null;
}

export function ScrollToTop() {
  const { hash, pathname } = useLocation();
  const lenis = useLenis();
  useEffect(() => {
    if (hash) {
      const target = document.getElementById(hash.slice(1));
      if (target) {
        const scrollToTarget = () => {
          if (lenis) {
            lenis.scrollTo(target, { immediate: true, force: true, offset: -104 });
            return;
          }
          target.scrollIntoView({ behavior: "instant", block: "start" });
        };
        scrollToTarget();
        const followUp = window.setTimeout(scrollToTarget, 320);
        return () => window.clearTimeout(followUp);
      }
    }
    if (lenis) {
      lenis.scrollTo(0, { immediate: true, force: true });
      return;
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [hash, lenis, pathname]);
  return null;
}

export function SmoothScrollRoot({ children }) {
  const reduceMotion = useReducedMotion();
  const options = useMemo(
    () => ({
      autoRaf: true,
      smoothWheel: !reduceMotion,
      syncTouch: false,
      lerp: reduceMotion ? 1 : 0.085,
      wheelMultiplier: 0.9,
      overscroll: true,
      anchors: reduceMotion ? true : { offset: -92, duration: 1.05 },
      stopInertiaOnNavigate: true,
    }),
    [reduceMotion],
  );

  return (
    <ReactLenis root options={options}>
      {children}
    </ReactLenis>
  );
}

export const NAV_GROUPS = [
  {
    id: "about",
    label: "About GRID",
    intro: "Who runs GRID, why it exists and what it has done.",
    links: [
      ["Our story", "/journey", "How two students started GRID", Flag],
      ["What we do", "/about", "The idea behind GRID in two minutes", Info],
      ["Our team", "/team", "The students who run it", UsersThree],
      ["Our impact", "/impact", "Numbers, with where they come from", ChartLineUp],
      ["Partners", "/partnerships", "Who we worked with, and how", Handshake],
      ["People we've met", "/meetings", "Visits and conversations", ChatCircleText],
      ["How we're run", "/governance", "Responsibility, safety and policies", Scales],
    ],
    feature: {
      title: "Watch the GRID film",
      text: "Our story in about a minute.",
      to: "/#film",
      image: AAROH_IMAGES.roomWide,
    },
  },
  {
    id: "programmes",
    label: "Programmes",
    intro: "Every programme says who led it and what GRID did.",
    cards: [
      ["AAROH", "/programmes/aaroh", "Run by GRID · IIIT Delhi", AAROH_IMAGES.room],
      ["Aurora", "/programmes/aurora", "Run by GRID · online, 27+ countries", "/images/evidence/jerovin-chillgui-02.jpg"],
      ["Metamorphosis", "/programmes/metamorphosis", "GRID as outreach partner · IIT Delhi", "/images/metamorphosis/album/meta-745-cohort-group-photo-800.webp"],
    ],
    links: [
      ["All programmes", "/programmes", "Everything we have run or supported", Path],
      ["Upcoming events", "/programmes/upcoming", "Dates, who can apply and fees", CalendarBlank],
    ],
  },
  {
    id: "learn",
    label: "Learn & build",
    intro: "Free help for students starting a project.",
    links: [
      ["Project guides", "/guides", "Step-by-step, from problem to pitch", BookOpenText],
      ["Problems to solve", "/problems", "Real issues worth working on", Target],
      ["Student projects", "/projects", "What students have built", Hammer],
      ["Free opportunities", "/student-opportunities", "Programmes and openings you can join", Compass],
      ["Stories", "/stories", "Notes from the rooms we've been in", Megaphone],
      ["Photos", "/media", "Every event, photographed", Images],
    ],
    feature: {
      title: "New here?",
      text: "Start with 'Find a real-world problem'.",
      to: "/guides/find-a-real-world-problem",
      image: AAROH_IMAGES.studentDiscussion,
    },
  },
];

export const JOIN_LINKS = [
  ["I'm a student", "/for-students", "Programmes and guides for you", Student],
  ["I'm from a school", "/for-schools", "Bring GRID to your students", Buildings],
  ["I want to mentor or partner", "/for-partners", "Offer time, problems or resources", Handshake],
  ["Join the team", "/student-opportunities/team", "Run a real part of GRID", RocketLaunch],
  ["Internships", "/student-opportunities/internships", "A scoped project with a clear output", Briefcase],
];

export function BrandMark({ compact = false }) {
  return (
    <Link className={`brand-mark ${compact ? "brand-mark--compact" : ""}`} to="/" aria-label="Project GRID home">
      <img src="/images/brand/grid-mark.png" alt="" width="704" height="414" />
    </Link>
  );
}

export function useNavTone() {
  const { pathname } = useLocation();
  const [state, setState] = useState({ dark: false, scrolled: false, clear: false });
  useEffect(() => {
    let frame = 0;
    const under = (selector) => [...document.querySelectorAll(selector)].some((element) => {
      const rect = element.getBoundingClientRect();
      return rect.top <= 36 && rect.bottom >= 36;
    });
    const check = () => {
      frame = 0;
      const dark = under("[data-nav-tone='dark']");
      const clear = dark && window.scrollY < 40 && under("[data-nav-clear]");
      const scrolled = window.scrollY > 8;
      setState((previous) => (previous.dark === dark && previous.scrolled === scrolled && previous.clear === clear ? previous : { dark, scrolled, clear }));
    };
    const late = window.setTimeout(check, 750);
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(check);
    };
    check();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.clearTimeout(late);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [pathname]);
  return state;
}

export function isPathActive(pathname, path) {
  const clean = path.split("#")[0];
  return pathname === clean || (clean !== "/" && pathname.startsWith(`${clean}/`));
}

export function NavLinkRow({ label, path, note, Icon, pathname }) {
  const current = isPathActive(pathname, path);
  return (
    <Link className={`nav-row ${current ? "is-current" : ""}`} to={path} aria-current={current ? "page" : undefined}>
      <span className="nav-row__icon" aria-hidden="true"><Icon weight="bold" /></span>
      <span className="nav-row__text">
        <strong>{label}</strong>
        <small>{note}</small>
      </span>
      <ArrowRight className="nav-row__arrow" weight="bold" aria-hidden="true" />
    </Link>
  );
}

export function NavPanel({ group, pathname }) {
  if (group.id === "join") {
    return (
      <div className="nav-panel__body nav-panel__body--join">
        <div className="nav-panel__list">
          {JOIN_LINKS.map(([label, path, note, Icon]) => (
            <NavLinkRow key={path} label={label} path={path} note={note} Icon={Icon} pathname={pathname} />
          ))}
        </div>
        <p className="nav-panel__foot">Not sure which? Write to <a href="mailto:founder@projectgrid.org">founder@projectgrid.org</a></p>
      </div>
    );
  }
  return (
    <div className={`nav-panel__body nav-panel__body--${group.id}`}>
      <p className="nav-panel__intro">{group.intro}</p>
      {group.cards && (
        <div className="nav-panel__cards">
          {group.cards.map(([title, path, role, image]) => (
            <Link key={path} to={path} className={`nav-card ${isPathActive(pathname, path) ? "is-current" : ""}`}>
              <span className="nav-card__img"><img src={image} alt="" loading="lazy" /></span>
              <strong>{title}</strong>
              <small>{role}</small>
            </Link>
          ))}
        </div>
      )}
      <div className="nav-panel__list">
        {group.links.map(([label, path, note, Icon]) => (
          <NavLinkRow key={path} label={label} path={path} note={note} Icon={Icon} pathname={pathname} />
        ))}
      </div>
      {group.feature && (
        <Link className="nav-feature" to={group.feature.to}>
          <span className="nav-feature__img"><img src={group.feature.image} alt="" loading="lazy" /></span>
          <strong>{group.feature.title}</strong>
          <small>{group.feature.text}</small>
        </Link>
      )}
    </div>
  );
}

export function SiteHeader() {
  const [openGroup, setOpenGroup] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const { dark, scrolled, clear } = useNavTone();
  const headerRef = useRef(null);
  const timer = useRef(0);
  const groups = [...NAV_GROUPS, { id: "join", label: "Join GRID" }];

  useEffect(() => {
    setOpenGroup(null);
    setMenuOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!openGroup && !menuOpen) return undefined;
    function onKey(event) {
      if (event.key === "Escape") {
        setOpenGroup(null);
        setMenuOpen(false);
      }
    }
    function onPointer(event) {
      if (!headerRef.current?.contains(event.target)) setOpenGroup(null);
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [openGroup, menuOpen]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [menuOpen]);

  const groupActive = (group) =>
    group.id === "join"
      ? JOIN_LINKS.some(([, path]) => isPathActive(location.pathname, path)) || location.pathname === "/get-involved"
      : [...(group.links ?? []), ...(group.cards ?? [])].some(([, path]) => isPathActive(location.pathname, path));
  const hoverOpen = (id) => {
    if (window.matchMedia("(hover: none)").matches) return;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpenGroup(id), openGroup ? 0 : 90);
  };
  const hoverClose = () => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setOpenGroup(null), 180);
  };
  const active = groups.find((group) => group.id === openGroup);

  return (
    <header
      ref={headerRef}
      className={`gnav ${dark && !menuOpen && !openGroup ? "gnav--dark" : ""} ${clear && !menuOpen && !openGroup ? "gnav--clear" : ""} ${scrolled || openGroup ? "gnav--solid" : ""} ${menuOpen ? "gnav--menu" : ""}`}
      onMouseLeave={hoverClose}
    >
      <div className="gnav__bar">
        <BrandMark />
        <nav className="gnav__links" aria-label="Main menu">
          {groups.map((group) => (
            <Fragment key={group.id}>
            {group.id === "join" && (
              <Link className={`gnav__plain ${location.pathname === "/contact" ? "is-active" : ""}`} to="/contact" onMouseEnter={hoverClose}>Contact</Link>
            )}
            <button
              type="button"
              className={`gnav__trigger ${group.id === "join" ? "gnav__trigger--join" : ""} ${groupActive(group) ? "is-active" : ""}`}
              aria-expanded={openGroup === group.id}
              aria-controls="gnav-panel"
              onMouseEnter={() => hoverOpen(group.id)}
              onFocus={() => openGroup && setOpenGroup(group.id)}
              onClick={() => setOpenGroup(openGroup === group.id ? null : group.id)}
            >
              <span>{group.label}</span>
              <CaretDown weight="bold" aria-hidden="true" />
            </button>
            </Fragment>
          ))}
        </nav>
        <button
          type="button"
          className="gnav__menu"
          aria-expanded={menuOpen}
          aria-controls="gnav-sheet"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <span className="gnav__menu-label">
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.span key={menuOpen ? "close" : "menu"} initial={reduceMotion ? false : { y: "100%" }} animate={{ y: "0%" }} exit={reduceMotion ? undefined : { y: "-100%" }} transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}>
                {menuOpen ? "Close" : "Menu"}
              </motion.span>
            </AnimatePresence>
          </span>
          <motion.span className="gnav__menu-icon" aria-hidden="true" animate={{ rotate: menuOpen ? 90 : 0 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}>
            {menuOpen ? <X weight="bold" /> : <List weight="bold" />}
          </motion.span>
        </button>
      </div>
      <AnimatePresence>
        {active && (
          <motion.div
            id="gnav-panel"
            key="panel"
            className="nav-panel"
            role="region"
            aria-label={active.label}
            onMouseEnter={() => window.clearTimeout(timer.current)}
            initial={reduceMotion ? false : { opacity: 0, y: -10, clipPath: "inset(0% 0% 100% 0% round 24px)" }}
            animate={{ opacity: 1, y: 0, clipPath: "inset(0% 0% 0% 0% round 24px)" }}
            exit={reduceMotion ? undefined : { opacity: 0, y: -6, transition: { duration: 0.16 } }}
            transition={{ duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={active.id}
                initial={reduceMotion ? false : { opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={reduceMotion ? undefined : { opacity: 0, x: -12 }}
                transition={{ duration: 0.22, ease: [0.25, 1, 0.5, 1] }}
              >
                <NavPanel group={active} pathname={location.pathname} />
              </motion.div>
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            id="gnav-sheet"
            className="gnav__sheet"
            variants={SHEET}
            initial={reduceMotion ? false : "hidden"}
            animate="show"
            exit={reduceMotion ? undefined : "hidden"}
          >
            <motion.div variants={SHEET_ITEM}>
              <Link className="gnav__sheet-cta" to="/get-involved">Join GRID <ArrowRight weight="bold" aria-hidden="true" /></Link>
            </motion.div>
            {NAV_GROUPS.map((group) => (
              <motion.section key={group.id} variants={SHEET_GROUP}>
                <motion.h2 variants={SHEET_ITEM}>{group.label}</motion.h2>
                {[...(group.cards ?? []), ...group.links].map(([label, path]) => (
                  <motion.div key={path} variants={SHEET_ITEM}>
                    <Link to={path} aria-current={isPathActive(location.pathname, path) ? "page" : undefined}>
                      <strong>{label}</strong>
                      <ArrowRight weight="bold" aria-hidden="true" />
                    </Link>
                  </motion.div>
                ))}
              </motion.section>
            ))}
            <motion.section variants={SHEET_GROUP}>
              <motion.h2 variants={SHEET_ITEM}>Join GRID</motion.h2>
              {JOIN_LINKS.map(([label, path]) => (
                <motion.div key={path} variants={SHEET_ITEM}>
                  <Link to={path}><strong>{label}</strong><ArrowRight weight="bold" aria-hidden="true" /></Link>
                </motion.div>
              ))}
              <motion.div variants={SHEET_ITEM}>
                <Link to="/contact"><strong>Contact</strong><ArrowRight weight="bold" aria-hidden="true" /></Link>
              </motion.div>
            </motion.section>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

// Mobile menu: the sheet wipes down from the bar, then its links rise in one after another.
const SHEET_EASE = [0.16, 1, 0.3, 1];
const SHEET = {
  hidden: { clipPath: "inset(0% 0% 100% 0%)", transition: { duration: 0.38, ease: [0.65, 0, 0.35, 1] } },
  show: { clipPath: "inset(0% 0% 0% 0%)", transition: { duration: 0.62, ease: SHEET_EASE, delayChildren: 0.1, staggerChildren: 0.06 } },
};
const SHEET_GROUP = { hidden: {}, show: { transition: { staggerChildren: 0.035 } } };
const SHEET_ITEM = {
  hidden: { opacity: 0, y: 26 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: SHEET_EASE } },
};

export function SiteFooter() {
  const reduceMotion = useReducedMotion();
  const stagger = (index) => ({
    initial: reduceMotion ? false : { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.8, delay: index * 0.06, ease: [0.16, 1, 0.3, 1] },
  });
  return (
    <footer className="gfoot" data-nav-tone="dark">
      <div className="gfoot__top">
        <motion.div className="gfoot__intro" {...stagger(0)}>
          <p>Project GRID is a student-led initiative helping school students turn ideas into projects.</p>
          <a className="gfoot__mail" href="mailto:founder@projectgrid.org">founder@projectgrid.org</a>
          <div className="gfoot__socials" aria-label="Social links">
            <a href="https://www.instagram.com/projectgrid_org/" target="_blank" rel="noreferrer" aria-label="Project GRID on Instagram"><InstagramLogo weight="bold" /></a>
            <a href="https://www.linkedin.com/company/project-grid-global" target="_blank" rel="noreferrer" aria-label="Project GRID on LinkedIn"><LinkedinLogo weight="bold" /></a>
            <a href="mailto:founder@projectgrid.org" aria-label="Email Project GRID"><EnvelopeSimple weight="bold" /></a>
          </div>
        </motion.div>
        {NAV_GROUPS.map((group, index) => (
          <motion.nav key={group.id} aria-label={group.label} {...stagger(index + 1)}>
            <span>{group.label}</span>
            {[...(group.cards ?? []), ...group.links].map(([label, path]) => <Link key={path} to={path.replace("/#film", "/")}>{label}</Link>)}
          </motion.nav>
        ))}
        <motion.nav aria-label="Take part" {...stagger(4)}>
          <span>Join GRID</span>
          {JOIN_LINKS.map(([label, path]) => <Link key={path} to={path}>{label}</Link>)}
          <Link to="/contact">Contact</Link>
        </motion.nav>
      </div>
      <Link className="gfoot__mark" to="/" aria-label="Project GRID home">
        <GridMark title="Project GRID" />
      </Link>
      <div className="gfoot__base">
        <span>© 2026 Project GRID</span>
        <span><Link to="/privacy">Privacy</Link><Link to="/terms">Terms</Link><Link to="/governance">Governance</Link></span>
      </div>
    </footer>
  );
}

export function PageFrame({ children, className = "" }) {
  return (
    <div className={`app-shell ${className}`}>
      {children}
      <PageEnding />
      <SiteFooter />
    </div>
  );
}

export const revealPresets = {
  soft: { opacity: 0.78, y: 28 },
  slide: { opacity: 0.7, x: -34 },
  scale: { opacity: 0.72, scale: 0.955 },
  wipe: { opacity: 0.86, y: 18, clipPath: "inset(0 0 18% 0)" },
};

export function Reveal({ children, className = "", delay = 0, preset = "soft" }) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduceMotion ? false : revealPresets[preset]}
      whileInView={
        reduceMotion
          ? undefined
          : { opacity: 1, x: 0, y: 0, scale: 1, clipPath: "inset(0 0 0% 0)" }
      }
      viewport={{ once: true, amount: 0.18 }}
      transition={{ duration: preset === "wipe" ? 0.84 : 0.68, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}

export function ImpactNumber({ value }) {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const isInView = useInView(ref, { once: true, amount: 0.65 });
  // Split "≈1,500+" or "₹1 lakh" or "251M" into prefix, number and suffix so every part survives the count.
  const match = String(value).match(/^([^\d]*)([\d,]*\.?\d+)(.*)$/);
  const prefix = match ? match[1] : "";
  const numericValue = match ? Number(match[2].replace(/,/g, "")) : 0;
  const suffix = match ? match[3] : "";
  const decimals = match && match[2].includes(".") ? match[2].split(".")[1].length : 0;
  const progress = useMotionValue(0);
  const displayValue = useTransform(
    progress,
    (latest) => `${prefix}${latest.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${suffix}`,
  );

  useEffect(() => {
    if (!isInView || reduceMotion) return undefined;
    const controls = animate(progress, numericValue, {
      duration: 1.25,
      ease: [0.16, 1, 0.3, 1],
    });
    return () => controls.stop();
  }, [isInView, numericValue, progress, reduceMotion]);

  if (reduceMotion || !match) return <strong>{value}</strong>;

  return (
    <>
      <span className="sr-only">{value}</span>
      <motion.strong ref={ref} aria-hidden="true">{displayValue}</motion.strong>
    </>
  );
}

export function SectionLabel({ index, children }) {
  return (
    <div className="section-label">
      <span>{index}</span>
      <span>{children}</span>
    </div>
  );
}

export function TextLink({ to, children, className = "" }) {
  return (
    <Link className={`text-link ${className}`} to={to}>
      {children} <ArrowRight weight="bold" aria-hidden="true" />
    </Link>
  );
}

export function ProgressRail() {
  const { scrollYProgress } = useScroll();
  const scaleY = useSpring(scrollYProgress, { stiffness: 90, damping: 22, restDelta: 0.001 });
  const anchors = [
    ["01", "Home", "#home"],
    ["02", "The idea", "#living-grid"],
    ["03", "What is GRID", "#about-grid"],
    ["04", "Programmes", "#programmes"],
    ["05", "Opportunities", "#opportunities"],
    ["06", "The team", "#team"],
    ["07", "Register", "#register"],
  ];
  return (
    <aside className="progress-rail" aria-label="Homepage sections">
      <div className="progress-rail__track">
        <motion.span style={{ scaleY }} />
      </div>
      {anchors.map(([number, label, href]) => (
        <a href={href} key={number}>
          <span>{number}</span>
          <span>{label}</span>
        </a>
      ))}
    </aside>
  );
}

export function Photo({
  src,
  alt,
  className = "",
  priority = false,
  sizes = "(max-width: 767px) 100vw, 50vw",
  style,
  width,
  height,
}) {
  const imageData = getImageData(src);
  const resolvedWidth = width ?? imageData?.width ?? 1800;
  const resolvedHeight = height ?? imageData?.height ?? 1202;
  const image = (
    <img
      className={className}
      src={src}
      alt={alt}
      width={resolvedWidth}
      height={resolvedHeight}
      loading={priority ? "eager" : "lazy"}
      fetchPriority={priority ? "high" : undefined}
      decoding={priority ? "auto" : "async"}
      sizes={imageData?.sources?.length ? sizes : undefined}
      style={style}
    />
  );

  if (!imageData?.sources?.length) return image;

  return (
    <picture className="responsive-picture">
      {imageData.sources.map((source) => (
        <source
          key={`${source.type}-${source.srcSet}`}
          type={source.type}
          srcSet={source.srcSet}
          sizes={sizes}
        />
      ))}
      {image}
    </picture>
  );
}

export const EASE_OUT = [0.16, 1, 0.3, 1];

export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const list = window.matchMedia(query);
    const update = () => setMatches(list.matches);
    update();
    list.addEventListener("change", update);
    return () => list.removeEventListener("change", update);
  }, [query]);
  return matches;
}

export function MaskWords({ as: Tag = "h2", text, className = "", id, delay = 0, onMount = false, accent }) {
  const reduceMotion = useReducedMotion();
  const words = text.split(" ");
  if (reduceMotion) {
    return (
      <Tag className={className} id={id}>
        {words.map((word, index) => (
          <span key={`${word}-${index}`} className={word === accent ? "is-accent" : undefined}>{word}{index < words.length - 1 ? " " : ""}</span>
        ))}
      </Tag>
    );
  }
  const reveal = onMount ? { animate: { y: "0%" } } : { whileInView: { y: "0%" }, viewport: { once: true, amount: 0.6 } };
  return (
    <Tag className={className} id={id} aria-label={text}>
      {words.map((word, index) => (
        <Fragment key={`${word}-${index}`}>
          <span className="mask-word" aria-hidden="true">
            <motion.span
              className={word === accent ? "is-accent" : undefined}
              initial={{ y: "110%" }}
              {...reveal}
              transition={{ duration: 1, delay: delay + index * 0.045, ease: EASE_OUT }}
            >
              {word}
            </motion.span>
          </span>
          {index < words.length - 1 ? " " : ""}
        </Fragment>
      ))}
    </Tag>
  );
}

export function ScrubWord({ word, progress, range }) {
  const opacity = useTransform(progress, (latest) => 0.14 + 0.86 * Math.min(1, Math.max(0, (latest - range[0]) / (range[1] - range[0]))));
  return <motion.span style={{ opacity }}>{word} </motion.span>;
}

export function ScrubStatement({ text }) {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.85", "end 0.5"] });
  const words = text.split(" ");
  if (reduceMotion) return <p className="gh-statement" ref={ref}>{text}</p>;
  return (
    <p className="gh-statement" ref={ref} aria-label={text}>
      <span aria-hidden="true">
        {words.map((word, index) => (
          <ScrubWord key={`${word}-${index}`} word={word} progress={scrollYProgress} range={[index / words.length, (index + 1) / words.length]} />
        ))}
      </span>
    </p>
  );
}

export function ClipPhoto({ src, alt, caption, className = "", sizes = "(max-width: 900px) 100vw, 50vw", priority = false }) {
  const reduceMotion = useReducedMotion();
  return (
    <figure className={`gh-photo ${className}`}>
      <motion.div
        className="gh-photo__frame"
        initial={reduceMotion ? false : { clipPath: "inset(18% 0% 0% 0% round 28px)" }}
        whileInView={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }}
        viewport={{ once: true, amount: 0.25 }}
        transition={{ duration: 1.1, ease: EASE_OUT }}
      >
        <motion.div
          className="gh-photo__zoom"
          initial={reduceMotion ? false : { scale: 1.18 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 1.3, ease: EASE_OUT }}
        >
          <Photo src={src} alt={alt} sizes={sizes} priority={priority} />
        </motion.div>
      </motion.div>
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
}

